import type { ReactNode } from 'react'
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react'

import axios, { AxiosHeaders } from 'axios'
import type { AxiosError, AxiosRequestConfig, RawAxiosHeaders } from 'axios'
import { useAtom, useSetAtom } from 'jotai'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'

import { getOwnProfileQueryOptions, type ApiError } from '@b2b/api'
import {
  canRefreshSession,
  clearRegistrationAuthSession,
  clearRegistrationSessionHandoff,
  clearRegistrationSessionId,
  getApiErrorMessage,
  refreshSessionToken,
} from '@b2b/shared'
import { envConfig } from '@twisto/base/env-config'
import { Loader } from '@twistopayments/design-system/components/atoms/loader'
import {
  AppError,
  parseStatus,
  useErrorModal,
} from '@twistopayments/design-system/components/molecules/error-boundary'
import { showDefaultError } from '@twistopayments/design-system/components/molecules/error-boundary/error-modal-builder'

import {
  clearOnboardingSessionHandoff,
  getPendingOnboardingSession,
} from '../../auth/session-handoff'
import { sessionAtom, type Session } from '../../auth/session'

import { applyOnboardingApiFallback } from './onboarding-api-fallback'

const queryClient = new QueryClient({
  defaultOptions: {
    queries: { staleTime: 0 },
  },
})

// envConfig derives the endpoint from the browser host, which points at the
// mock server locally; the override lets a dev run against a real API, same as
// the flow app does.
axios.defaults.baseURL =
  process.env.NEXT_PUBLIC_API_URL ?? envConfig.B2B_API_ENDPOINT

type SetSession = (value: Session | ((previous: Session) => Session)) => void

type ShowError = ReturnType<typeof useErrorModal>['showError']

type AuthRefreshRequestConfig = AxiosRequestConfig & {
  _retry?: boolean
  skipAuthRefresh?: boolean
  skipGlobalErrorHandler?: boolean
  skipSessionRecovery?: boolean
  skipGlobalErrorStatuses?: number[]
}

type ActiveSession = Exclude<Session, null>

// Refresh the registration session after onboarding so the customer app gets
// a token containing the tenant claims bound during the flow.
export const refreshHandoffSession = async (
  handoffSession: ActiveSession
): Promise<ActiveSession> => {
  if (!handoffSession.refreshToken) {
    throw new Error('The onboarding handoff has no refresh token')
  }

  const refreshedTokens = await refreshSessionToken(handoffSession.refreshToken)

  return {
    accessToken: refreshedTokens.accessToken,
    refreshToken: refreshedTokens.refreshToken ?? handoffSession.refreshToken,
    source: handoffSession.source,
  }
}

const setAuthorizationHeader = (
  config: AuthRefreshRequestConfig,
  accessToken: string
) => {
  const authorization = `Bearer ${accessToken}`

  config.headers = AxiosHeaders.from(
    config.headers as RawAxiosHeaders | AxiosHeaders | undefined
  )
  config.headers.set('Authorization', authorization)
}

const isRecoverable401 = (
  error: AxiosError,
  config: AuthRefreshRequestConfig,
  hasRefreshToken: boolean
) =>
  error.response?.status === 401 &&
  hasRefreshToken &&
  !config._retry &&
  !config.skipAuthRefresh

const refreshSessionAndRetry = async (
  refreshToken: string,
  config: AuthRefreshRequestConfig,
  setSession: SetSession
) => {
  config._retry = true

  try {
    const refreshedTokens = await refreshSessionToken(refreshToken)

    setSession((previous) =>
      previous
        ? {
            ...previous,
            accessToken: refreshedTokens.accessToken,
            refreshToken: refreshedTokens.refreshToken ?? refreshToken,
          }
        : null
    )
    setAuthorizationHeader(config, refreshedTokens.accessToken)

    return axios(config)
  } catch {
    return null
  }
}

const clearSession = (setSession: SetSession) => {
  delete axios.defaults.headers.common['Authorization']
  setSession(null)
  clearRegistrationAuthSession()
  clearRegistrationSessionHandoff()
  clearRegistrationSessionId()
  queryClient.clear()
}

const logout = (setSession: SetSession) => {
  clearSession(setSession)
  window.location.replace('/auth/login/')
}

const toShowErrorArg = (appError: AppError) =>
  appError as unknown as Parameters<ShowError>[0]

const notifyGlobalError = (
  error: AxiosError<ApiError>,
  showError: ShowError
) => {
  const message = getApiErrorMessage(error.response?.data)
  const modalErrorProps = message
    ? showDefaultError(message)
    : parseStatus(error.response?.status)

  showError(toShowErrorArg(new AppError(message ?? '', modalErrorProps)))
}

const useAxiosInterceptors = ({
  accessToken,
  canRefresh,
  refreshToken,
  setSession,
  showError,
  useUnavailableApiFallback,
}: {
  accessToken: string | null
  canRefresh: boolean
  refreshToken: string | null
  setSession: SetSession
  showError: ShowError
  useUnavailableApiFallback: boolean
}) => {
  useEffect(() => {
    const requestInterceptorId = axios.interceptors.request.use((config) => {
      if (accessToken && !config.headers.has('Authorization')) {
        config.headers.set('Authorization', `Bearer ${accessToken}`)
      }

      return useUnavailableApiFallback
        ? applyOnboardingApiFallback(config)
        : config
    })

    const handleResponseError = async (error: AxiosError<ApiError>) => {
      const config = error.config as AuthRefreshRequestConfig | undefined
      const shouldRecoverSession =
        error.response?.status === 401 &&
        Boolean(accessToken) &&
        !config?.skipAuthRefresh &&
        !config?.skipSessionRecovery

      if (
        config &&
        shouldRecoverSession &&
        canRefresh &&
        isRecoverable401(error, config, Boolean(refreshToken))
      ) {
        const retried = await refreshSessionAndRetry(
          refreshToken!,
          config,
          setSession
        )

        if (retried) {
          return retried
        }

        logout(setSession)
        return Promise.reject(error)
      }

      if (shouldRecoverSession) {
        logout(setSession)
        return Promise.reject(error)
      }

      const skipGlobalError =
        config?.skipGlobalErrorHandler ||
        config?.skipGlobalErrorStatuses?.includes(error.response?.status ?? 0)

      if (!skipGlobalError && !axios.isCancel(error)) {
        notifyGlobalError(error, showError)
      }

      return Promise.reject(error)
    }

    const responseInterceptorId = axios.interceptors.response.use(
      (response) => response,
      handleResponseError
    )

    return () => {
      axios.interceptors.request.eject(requestInterceptorId)
      axios.interceptors.response.eject(responseInterceptorId)
    }
  }, [
    accessToken,
    canRefresh,
    refreshToken,
    setSession,
    showError,
    useUnavailableApiFallback,
  ])
}

type SessionStatus = 'anonymous' | 'authenticated' | 'error' | 'loading'

type SessionContextValue = {
  status: Exclude<SessionStatus, 'loading'>
}

const SessionContext = createContext<SessionContextValue | null>(null)

const isTerminalProfileError = (error: AxiosError) =>
  error.response?.status === 401 ||
  error.response?.status === 403 ||
  error.response?.status === 404 ||
  error.response?.status === 410

const RestApiEffects = ({ children }: { children: ReactNode }) => {
  const [session, setSession] = useAtom(sessionAtom)
  const { showError } = useErrorModal()
  const [status, setStatus] = useState<SessionStatus>('loading')
  const [validatedAccessToken, setValidatedAccessToken] = useState<
    string | null
  >(null)
  const canRefresh = canRefreshSession(session?.refreshToken ?? null)
  // TODO(B2B API migration): Remove the development-wide fallback after the
  // Overview no longer calls legacy APIs that are absent from env.local.
  const useUnavailableApiFallback =
    session?.source === 'onboarding' || envConfig.RUNTIME === 'development'

  useAxiosInterceptors({
    accessToken: session?.accessToken ?? null,
    canRefresh,
    refreshToken: session?.refreshToken ?? null,
    setSession,
    showError,
    useUnavailableApiFallback,
  })

  useEffect(() => {
    const handoffSession = getPendingOnboardingSession()

    if (handoffSession) {
      let active = true

      setStatus('loading')
      setValidatedAccessToken(null)
      void refreshHandoffSession(handoffSession)
        .then((refreshedSession) => {
          if (!active) return

          setSession(refreshedSession)
          clearOnboardingSessionHandoff()
        })
        .catch((reason: unknown) => {
          if (!active) return

          const error = reason as AxiosError<ApiError>
          setStatus('error')
          notifyGlobalError(error, showError)
        })

      return () => {
        active = false
      }
    }

    if (!session?.accessToken) {
      setValidatedAccessToken(null)
      setStatus('anonymous')
      return
    }

    let active = true

    setStatus('loading')

    // fetchQuery, not a bare request: it populates the cache the screen's own
    // profile observers read, and it dedupes — otherwise this effect running
    // twice (React StrictMode in development) fires the request twice.
    queryClient
      .fetchQuery(
        getOwnProfileQueryOptions({
          axios: {
            headers: { Authorization: `Bearer ${session.accessToken}` },
            skipGlobalErrorHandler: true,
          } as AuthRefreshRequestConfig,
        })
      )
      .then(() => {
        if (!active) return

        setValidatedAccessToken(session.accessToken)
        setStatus('authenticated')
      })
      .catch((reason: unknown) => {
        if (!active) return

        const error = reason as AxiosError<ApiError>
        if (isTerminalProfileError(error)) {
          clearSession(setSession)
          setValidatedAccessToken(null)
          setStatus('anonymous')
          window.location.replace('/auth/login/')
          return
        }

        setStatus('error')
        notifyGlobalError(error, showError)
      })

    return () => {
      active = false
    }
  }, [session?.accessToken, setSession, showError])

  const effectiveStatus =
    session?.accessToken &&
    validatedAccessToken !== session.accessToken &&
    status !== 'error'
      ? 'loading'
      : status
  const contextValue = useMemo<SessionContextValue | null>(
    () => (effectiveStatus === 'loading' ? null : { status: effectiveStatus }),
    [effectiveStatus]
  )

  if (effectiveStatus === 'loading') {
    return <Loader />
  }

  return (
    <SessionContext.Provider value={contextValue}>
      {children}
    </SessionContext.Provider>
  )
}

type Props = {
  children: ReactNode
}

export const RestApiProvider = ({ children }: Props) => (
  <QueryClientProvider client={queryClient}>
    <RestApiEffects>{children}</RestApiEffects>
  </QueryClientProvider>
)

export const useCustomerSession = () => {
  const context = useContext(SessionContext)

  if (!context) {
    throw new Error('useCustomerSession must be used within RestApiProvider')
  }

  return context
}

// The platform has no logout endpoint, so signing out is browser-local: the
// access token keeps working until it expires.
export const useLogout = () => {
  const setSession = useSetAtom(sessionAtom)

  return useCallback(() => logout(setSession), [setSession])
}
